import './BuildRecommendationTab.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import {
  AgeRange,
  AudienceInsightsDimensions,
  Gender,
  GoogleAudience,
  GoogleGeoLocation
} from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Intent, Size } from '@Components/designConstants';
import { Radio } from '@Components/FormCheck';
import { Dropdown } from '@Components/Dropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { SearchCombobox } from '@Components/SearchCombobox';
import { calculateCPM } from '@src/utils/metric-utils';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { awaitJSON, YoutubePlanningLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { Plan } from './Plan';
import { PoweredByGoogle } from './PoweredByGoogle';
import {
  AGE_KEYS,
  ALL_STATES,
  ALL_STATES_ID,
  ALL_STATES_VALUE,
  GENDER_KEYS,
  GOAL_OPTIONS,
  INTEREST_SEARCH_DIMENSIONS,
  isAgeKey
} from './youtube-planning-constants';
import {
  buildTableRow,
  isAgeRangeArrConsecutive,
  mapToAudienceInsightMenuItem,
  mapToGoogleAudience,
  parseNumberFromMoney
} from './youtube-planning-utils';
import {
  AudienceInsightMenuItem,
  BuildRecommendationPrefill,
  GoogleAdsAccount,
  YouTubePlanData,
  YouTubePlanTableData,
  YouTubeProductSpecificForecasts
} from './youtube-planning-types';
import {
  fetchAudienceInsights,
  useAudienceSegmentsMasterList,
  usePrefillInterestMatch
} from './hooks/use-audience-insights';
import { useTargetingLocations } from './hooks/use-targeting-locations';

interface ReachForecastRow {
  costMicros: number;
  forecast: { onTargetReach: string; views: string };
}

interface PlanResponse {
  reachCurve: { reachForecasts: ReachForecastRow[] };
  closestDataPointToBudget?: {
    costMicros: number;
    forecast?: { onTargetReach?: string; onTargetImpressions?: string };
    plannedProductReachForecasts: YouTubeProductSpecificForecasts[];
  };
  onTargetAudienceMetrics?: { youtubeAudienceSize?: string };
  targetAffinities: GoogleAudience[];
  targetInMarkets: GoogleAudience[];
}

interface BuildRecommendationTabProps {
  adsAccount?: GoogleAdsAccount;
  // Starting values from a Creator Set. Read once, when the tab mounts.
  prefill?: BuildRecommendationPrefill;
}

export const BuildRecommendationTab = ({ adsAccount, prefill }: BuildRecommendationTabProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  const [goal, setGoal] = useState<string>();
  const [selectedAttribute, setSelectedAttribute] = useState<AudienceInsightMenuItem>();
  const [attributeOptionsCache, setAttributeOptionsCache] = useState(
    () => new Map<string, AudienceInsightMenuItem>()
  );
  const [prefillInterest] = useState(prefill?.interest);
  const [budget, setBudget] = useState<number>();
  const [dates, setDates] = useState({ start: '', end: '' });
  const [gender, setGender] = useState(prefill?.gender);
  const [ageRangeArr, setAgeRangeArr] = useState(prefill?.ageRange ?? []);
  const [targetLocationArr, setTargetLocationArr] = useState<GoogleGeoLocation[]>([]);
  const [generating, setGenerating] = useState<boolean>(false);
  const [recommendationData, setRecommendationData] = useState<YouTubePlanData>();
  const [showRecommendation, setShowRecommendation] = useState<boolean>(false);

  const { data: locations, error: locationsError } = useTargetingLocations();
  const masterLocationArr = useMemo(() => locations ?? [], [locations]);
  const { data: segments } = useAudienceSegmentsMasterList(cid, adsAccount);

  useEffect(() => {
    if (locationsError) {
      setError({
        message: `Failed to fetch Master Targeting Locations: ${locationsError.message}`,
        reportError: locationsError
      });
    }
  }, [locationsError, setError]);

  const isAgeRangeArrValid = useMemo(() => isAgeRangeArrConsecutive(ageRangeArr), [ageRangeArr]);

  // Wraps fetchAudienceInsights so the searchable Dropdown's onChange (which only returns the
  // selected option's value) can be resolved back into the full AudienceInsightMenuItem.
  const fetchGoogleAttributes = useCallback(
    async (input: string) => {
      const items = await fetchAudienceInsights(cid, adsAccount, input, INTEREST_SEARCH_DIMENSIONS);
      setAttributeOptionsCache(prev => {
        const next = new Map(prev);
        items.forEach(item => next.set(item.value, item));
        return next;
      });
      return items;
    },
    [cid, adsAccount]
  );

  const { data: prefillMatch } = usePrefillInterestMatch(cid, adsAccount, prefillInterest);

  useEffect(() => {
    if (!prefillMatch) return;
    setAttributeOptionsCache(prev => new Map(prev).set(prefillMatch.value, prefillMatch));
    setSelectedAttribute(current => current ?? prefillMatch);
  }, [prefillMatch]);

  const generateRecommendation = useCallback(
    async (
      editedBudget?: number,
      editedBudgetShare?: Record<string, number>,
      editedInterests?: Record<AudienceInsightsDimensions, AudienceInsightMenuItem[]>
    ) => {
      setGenerating(true);
      try {
        const res = await YoutubePlanningLambdaFetch('/createPlan', {
          method: 'POST',
          body: {
            company: cid,
            googleAdsAccount: adsAccount?.account_id,
            mccId: adsAccount?.mcc_id,
            attribute: selectedAttribute,
            goal,
            budget: editedBudget || budget,
            gender,
            ageRange: ageRangeArr,
            targetLocations: targetLocationArr.filter(loc => loc.id !== ALL_STATES_ID),
            targetAudiences: editedInterests
              ? Object.fromEntries(
                  Object.entries(editedInterests).map(([key, value]) => [
                    key,
                    mapToGoogleAudience(value)
                  ])
                )
              : undefined,
            startDate: dates.start,
            endDate: dates.end,
            budgetSplit: editedBudgetShare || null
          }
        });
        const plan = await awaitJSON<PlanResponse>(res);

        const reachForecasts = plan.reachCurve?.reachForecasts ?? [];
        const spendVsReach = reachForecasts.map(row => ({
          spend: row.costMicros / 1000000,
          val: parseInt(row.forecast.onTargetReach, 10)
        }));

        const spendVsViews = reachForecasts.map(row => ({
          spend: row.costMicros / 1000000,
          val: parseInt(row.forecast.views, 10)
        }));

        const suggestedBudget = (plan.closestDataPointToBudget?.costMicros ?? 0) / 1000000;
        const onTargetReach = parseInt(
          plan.closestDataPointToBudget?.forecast?.onTargetReach ?? '0',
          10
        );
        const onTargetImpressions = parseInt(
          plan.closestDataPointToBudget?.forecast?.onTargetImpressions ?? '0',
          10
        );

        const targetedSegments = {
          [AudienceInsightsDimensions.AFFINITY_USER_INTEREST]: mapToAudienceInsightMenuItem(
            plan.targetAffinities
          ),
          [AudienceInsightsDimensions.IN_MARKET_USER_INTEREST]: mapToAudienceInsightMenuItem(
            plan.targetInMarkets
          )
        };

        const productForecasts = plan.closestDataPointToBudget?.plannedProductReachForecasts ?? [];
        const tableData: YouTubePlanTableData[] = [];
        let rowId = 1;

        if (productForecasts[0]?.plannableProductCode === 'VIDEO_REACH_CAMPAIGN') {
          const includeVideoReach = editedBudgetShare == null || Boolean(editedBudgetShare['1']);
          const includeBumpers = editedBudgetShare == null || Boolean(editedBudgetShare['2']);

          if (includeVideoReach) {
            tableData.push(
              buildTableRow(productForecasts[0], suggestedBudget, rowId, 'Video Reach Campaign')
            );
            rowId += 1;
          }
          if (includeBumpers) {
            // Bumpers sit at index 0 if Video Reach was excluded, otherwise at index 1.
            const bumperIdx = includeVideoReach ? 1 : 0;
            tableData.push(
              buildTableRow(productForecasts[bumperIdx], suggestedBudget, rowId, 'Bumpers')
            );
            rowId += 1;
          }
        } else if (productForecasts[0]) {
          const format =
            goal === 'Maximize Views (Considerations)'
              ? 'Video View Campaign'
              : 'Demand Gen Campaign';
          tableData.push(buildTableRow(productForecasts[0], suggestedBudget, rowId, format));
          rowId += 1;
        }

        const planData: YouTubePlanData = {
          goal: goal || '',
          inputBudget: editedBudget || budget || 0,
          queryString: selectedAttribute?.label || '',
          gender: gender != null ? Gender[gender] : '',
          ageRange: ageRangeArr,
          targetLocation: targetLocationArr,
          dates: dates || {},
          suggestedBudget,
          onTargetReach,
          averageFreq: onTargetReach ? onTargetImpressions / onTargetReach : 0,
          cpm: calculateCPM(suggestedBudget, onTargetImpressions),
          youTubePopulation: parseInt(plan.onTargetAudienceMetrics?.youtubeAudienceSize ?? '0', 10),
          spendVsReach,
          spendVsViews,
          tableData,
          targetedSegments,
          masterSegments: {
            [AudienceInsightsDimensions.AFFINITY_USER_INTEREST]: segments?.affinity ?? [],
            [AudienceInsightsDimensions.IN_MARKET_USER_INTEREST]: segments?.inMarket ?? []
          }
        };
        setRecommendationData(planData);
        setShowRecommendation(true);
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setGenerating(false);
      }
    },
    [
      cid,
      adsAccount,
      selectedAttribute,
      goal,
      budget,
      dates,
      gender,
      ageRangeArr,
      targetLocationArr,
      segments,
      setError
    ]
  );

  // Disable any option that would break contiguity: once one or more ranges are selected,
  // only the already-selected ranges (so they can be deselected) and the ranges immediately
  // adjacent to the current min/max stay pickable. UNSPECIFIED ("All Ages") is mutually
  // exclusive with every other range in either direction.
  const ageRangeOptions = useMemo(() => {
    const isUnspecifiedSelected = ageRangeArr.includes('UNSPECIFIED');
    const selectedIndices = ageRangeArr
      .filter(key => key !== 'UNSPECIFIED')
      .map(key => AGE_KEYS.indexOf(key));
    const minIdx = selectedIndices.length ? Math.min(...selectedIndices) : undefined;
    const maxIdx = selectedIndices.length ? Math.max(...selectedIndices) : undefined;

    return AGE_KEYS.map((key, idx) => {
      if (key === 'UNSPECIFIED') {
        return { value: key, label: AgeRange[key], disabled: selectedIndices.length > 0 };
      }
      const isSelected = selectedIndices.includes(idx);
      const extendsRange = minIdx !== undefined && (idx === minIdx - 1 || idx === maxIdx! + 1);
      return {
        value: key,
        label: AgeRange[key],
        disabled:
          isUnspecifiedSelected || (selectedIndices.length > 0 && !isSelected && !extendsRange)
      };
    });
  }, [ageRangeArr]);

  const handleAgeRangeChange = useCallback((selected: string[]) => {
    if (selected.length === 0) {
      setAgeRangeArr([]);
      return;
    }
    if (selected.includes('UNSPECIFIED')) {
      setAgeRangeArr(['UNSPECIFIED']);
      return;
    }
    setAgeRangeArr(selected.filter(isAgeKey));
  }, []);

  const stateOptions = useMemo(
    () =>
      targetLocationArr.some(loc => loc.id === ALL_STATES_ID)
        ? [{ value: ALL_STATES_VALUE, label: ALL_STATES.name }]
        : masterLocationArr.map(loc => ({
            value: String(loc.id),
            label: loc.name
          })),
    [masterLocationArr, targetLocationArr]
  );

  const handleStatesChange = useCallback(
    (values: string[]) => {
      if (values.length === 0) {
        setTargetLocationArr([]);
        return;
      }
      if (values.includes(ALL_STATES_VALUE)) {
        setTargetLocationArr([ALL_STATES]);
        return;
      }
      setTargetLocationArr(
        values
          .map(v => masterLocationArr.find(loc => String(loc.id) === v))
          .filter((loc): loc is GoogleGeoLocation => loc != null)
      );
    },
    [masterLocationArr]
  );

  const onClose = useCallback(
    () =>
      setAreYouSure({
        title: 'Close Plan',
        message: 'You are about to close this plan, are you sure?',
        intent: Intent.WARNING,
        promise: false,
        cancelText: 'Never mind',
        okayText: 'Yes',
        onOkay: () => {
          setShowRecommendation(false);
        }
      }),
    [setAreYouSure]
  );

  const generateDisabled =
    !goal ||
    !adsAccount ||
    !budget ||
    !dates.start ||
    !dates.end ||
    !selectedAttribute ||
    !gender ||
    !isAgeRangeArrValid ||
    targetLocationArr.length === 0;

  if (showRecommendation) {
    return (
      <div className='build-recommendation-tab'>
        <Plan
          planData={recommendationData}
          onClose={onClose}
          onRegenerate={generateRecommendation}
          cid={cid}
        />
      </div>
    );
  }

  return (
    <div className='build-recommendation-tab'>
      <div className='planning-form' data-testid='build-recommendation-form'>
        <div className='planning-form-header'>
          <div className='header'>Build Recommendation</div>
          <div className='sub-header'>
            <PoweredByGoogle />
          </div>
        </div>
        <div className='planning-form-body'>
          <div className='planning-form-section'>
            <div className='planning-form-section-title'>Goal</div>
            <div className='goal-radio-choice'>
              {GOAL_OPTIONS.map(goalName => (
                <Radio
                  key={goalName}
                  className='goal-radio-option'
                  label={goalName}
                  checked={goal === goalName}
                  id={goalName}
                  onChange={() => setGoal(goalName)}
                />
              ))}
            </div>
          </div>

          <div className='planning-form-section' data-testid='build-audience-interest'>
            <div className='planning-form-section-title'>
              Audience Interest
              <InfoTooltip size={Size.SM}>
                Audience Interest guides targeting by finding people most likely to engage with the
                topic — even if they fall outside its main category. Please select one Audience
                Interest to align your plan towards.
              </InfoTooltip>
            </div>
            <SearchCombobox
              className='query-input'
              placeholder='Search for a category or company name...'
              value={selectedAttribute?.value ?? null}
              onChange={val =>
                setSelectedAttribute(val ? attributeOptionsCache.get(val) : undefined)
              }
              optionFetcher={fetchGoogleAttributes}
              formatSelectedLabel={val => attributeOptionsCache.get(val)?.label ?? val}
              disabled={!adsAccount}
            />
          </div>

          <div className='planning-form-section'>
            <div className='planning-form-section-title'>Budget</div>
            <Form.Control
              className='budget-input'
              placeholder='Input Budget'
              value={budget != null ? `$${budget.toLocaleString('en-US')}` : ''}
              onChange={e => {
                const parsedNumber = parseNumberFromMoney(e.target.value);
                if (parsedNumber != null && parsedNumber > 0) {
                  setBudget(parsedNumber);
                } else {
                  setBudget(undefined);
                }
              }}
            />
          </div>

          <div className='planning-form-section'>
            <div className='planning-form-section-title'>Audience Targeting</div>
            <div className='audience-targeting-container'>
              <div className='audience-targeting-dropdown-container' data-testid='build-gender'>
                <div className='dropdown-title'>Gender</div>
                <Dropdown
                  size={Size.LG}
                  options={GENDER_KEYS.map(key => ({ value: key, label: Gender[key] }))}
                  onChange={val => setGender(val)}
                  value={gender}
                  placeholder='Gender'
                  className='audience-targeting-dropdown'
                />
              </div>
              <div className='audience-targeting-dropdown-container' data-testid='build-age-range'>
                <div className='dropdown-title'>Age Range</div>
                <Dropdown
                  size={Size.LG}
                  options={ageRangeOptions}
                  onChange={handleAgeRangeChange}
                  value={ageRangeArr}
                  placeholder='Age Range'
                  className='audience-targeting-dropdown'

                  multiSelect
                  clearable
                />
                <div className='invalid-select' aria-hidden={isAgeRangeArrValid}>
                  Age Range Must Be Consecutive
                </div>
              </div>
              <div className='audience-targeting-dropdown-container'>
                <div className='dropdown-title'>Targeted States</div>
                <Dropdown
                  size={Size.LG}
                  options={stateOptions}
                  onChange={handleStatesChange}
                  value={targetLocationArr.map(loc => String(loc.id))}
                  placeholder='States'
                  className='audience-targeting-dropdown'

                  multiSelect
                  clearable
                  searchable
                />
              </div>
            </div>
          </div>
          <div className='planning-form-section'>
            <div className='planning-form-section-title'>Date Range</div>
            <DatePicker
              range={dates}
              isOutsideRange={date => date < TODAY}
              onChange={({ start, end }) => {
                setDates({ start, end });
              }}
            />
          </div>
        </div>

        <div className='planning-form-footer'>
          <Button
            size={Size.LG}
            className='generate-button'
            disabled={generateDisabled}
            loading={generating}
            onClick={() => generateRecommendation()}
          >
            Generate Recommendation
          </Button>
        </div>
      </div>
    </div>
  );
};
